/**
 * Voice Commands Utility
 *
 * Detects spoken commands (navigation, formatting, macros, templates) and
 * orchestrates how they're dispatched to the editor/sidebar.
 *
 * NOTE: Template/macro matching → `findBestTemplate` in `./voiceTemplateEngine.js`.
 * "Go to" heading navigation → `findBestHeading` in `./voiceHeadingEngine.js`.
 * Static editor commands → `./voiceCommandMatching.js` (leading phrase + fuzzy match).
 * Template/macro triggers ("Template", "Macro", …) are matched case-insensitively too.
 * Voice **macro** commands (after "Short" / "Macro" / "קיצור" / etc.) use the
 * same engine: macros are matched as pseudo-templates (`templateName` = macro name).
 */
import {
  findBestTemplate,
  findBestTemplateWithLeadingPrefixSkip,
  MIN_TEMPLATE_SCORE,
  MIN_TRIGGERLESS_SCORE,
  TRIGGERLESS_MAX_PREFIX_SKIP,
  TRIGGERLESS_WINDOW,
} from "./voiceTemplateEngine";
import {
  consumeLeadingTriggerPhrases,
  findLeadingStaticCommand,
  normalizeCommandTranscript,
} from "./voiceCommandMatching";

export { normalizeTranscript } from "./voiceCommandMatching";

// Voice commands mapping for different languages
const VOICE_COMMANDS = {
  // Navigation commands
  navigation: {
    "go to": {
      en: ["go to"],
      es: ["ir a", "ve a"],
      he: ["לך ל", "לך"],
      ar: ["اذهب إلى"],
      fr: ["va à", "aller à"],
    },
  },

  // Delete commands
  delete: {
    word: {
      en: ["delete word"],
      es: ["borrar palabra", "eliminar palabra"],
      he: ["מחק מילה", "מרחק מילה"], // "מרחק מילה" = common STT mishearing of "מחק מילה"
      ar: ["احذف كلمة"],
      fr: [
        "supprimer mot",
        "effacer mot",
        "supprimez mot",
        "supprimez mots",
        "supprimezmoi",
        "supprimezvous",
        "effacez mot",
        "effacez mots",
      ],
    },
    line: {
      en: ["delete line"],
      es: ["borrar línea", "eliminar línea"],
      he: ["מחק שורה", "מרחק שורה"], // "מרחק שורה" = common STT mishearing of "מחק שורה"
      ar: ["احذف سطر"],
      fr: [
        "supprimer ligne",
        "effacer ligne",
        "supprimez ligne",
        "supprimez lignes",
        "effacez ligne",
        "effacez lignes",
      ],
    },
    all: {
      en: ["delete all"],
      es: ["borrar todo", "eliminar todo"],
      he: ["מחק הכל", "מחק הכול", "מרחק הקול"], // "הכול" = alternate spelling; "מרחק הקול" = STT mishearing of "מחק הכל"
      ar: ["احذف الكل"],
      fr: ["supprimer tout", "effacer tout", "supprimez tout", "effacez tout"],
    },
  },

  // Undo/Redo commands
  undo: {
    en: ["undo","undue","endo"],
    es: ["deshacer"],
    he: ["בטל"],
    ar: ["تراجع"],
    fr: ["annuler", "annulez"],
  },
  redo: {
    en: ["redo"],
    es: ["rehacer"],
    he: ["חזור"],
    ar: ["كرر"],
    fr: ["rétablir", "refaire"],
  },

  // Text case commands
  case: {
    uppercase: {
      en: ["uppercase", "upper case", "upper-case"],
      es: ["mayúsculas"],
      he: ["אותיות גדולות"],
      ar: ["أحرف كبيرة"],
      fr: ["majuscules", "en majuscules", "majuscule"],
    },
    lowercase: {
      en: ["lowercase", "lower case", "lower-case"],
      es: ["minúsculas"],
      he: ["אותיות קטנות"],
      ar: ["أحرف صغيرة"],
      fr: ["minuscules", "en minuscules", "minuscule"],
    },
  },

  // Formatting commands
  formatting: {
    bold: {
      en: ["bold"],
      es: ["negrita"],
      he: ["מודגש"],
      ar: ["عريض"],
      fr: ["gras", "en gras"],
    },
  },

  // Line/Paragraph commands
  line: {
    newLine: {
      en: ["new line", "line"],
      es: ["nueva línea", "línea"],
      he: ["שורה חדשה", "שורה"],
      ar: ["سطر جديد", "سطر"],
      fr: ["nouvelle ligne", "ligne"],
    },
    paragraph: {
      en: ["paragraph", "new paragraph", "newparagraph"],
      es: ["párrafo", "nuevo párrafo"],
      he: ["פסקה", "פסקה חדשה"],
      ar: ["فقرة", "فقرة جديدة"],
      fr: ["paragraphe", "nouveau paragraphe"],
    },
  },

  // Selection commands
  selection: {
    currentSentence: {
      en: [
        "select current sentence",
        "select the current sentence",
        "select sentence",
        "select the sentence",
      ],
      es: [
        "seleccionar oración actual",
        "seleccionar la oración actual",
        "seleccionar oración",
      ],
      he: [
        "בחר משפט נוכחי",
        "בחר את המשפט הנוכחי",
        "בחר המשפט הנוכחי",
        "בחר משפט",
      ],
      ar: ["اختر الجملة الحالية"],
      fr: [
        "sélectionner phrase actuelle",
        "sélectionne phrase actuelle",
        "sélectionnez phrase actuelle",
      ],
    },
    currentParagraph: {
      en: [
        "select current paragraph",
        "select the current paragraph",
        "select paragraph",
        "select the paragraph",
      ],
      es: [
        "seleccionar párrafo actual",
        "seleccionar el párrafo actual",
        "seleccionar párrafo",
        "seleccionar el párrafo",
      ],
      he: [
        "בחר פסקה נוכחית",
        "בחר את הפסקה הנוכחית",
        "בחר הפסקה הנוכחית",
        "בחר פסקה",
        "בחר את הפסקה",
      ],
      ar: ["اختر الفقرة الحالية", "اختر الفقرة"],
      fr: [
        "sélectionner paragraphe actuel",
        "sélectionner paragraphe",
        "sélectionnez paragraphe actuel",
        "sélectionnez paragraphe",
      ],
    },
    nextSentence: {
      en: ["select next sentence", "select the next sentence"],
      es: [
        "seleccionar siguiente oración",
        "seleccionar la siguiente oración",
      ],
      he: ["בחר משפט הבא", "בחר את המשפט הבא", "בחר המשפט הבא"],
      ar: ["اختر الجملة التالية"],
      fr: ["sélectionner phrase suivante", "sélectionnez phrase suivante"],
    },
    previousSentence: {
      en: [
        "select previous sentence",
        "select the previous sentence",
      ],
      es: [
        "seleccionar oración anterior",
        "seleccionar la oración anterior",
      ],
      he: ["בחר משפט קודם", "בחר את המשפט הקודם", "בחר המשפט הקודם"],
      ar: ["اختر الجملة السابقة"],
      fr: ["sélectionner phrase précédente", "sélectionnez phrase précédente"],
    },
    nextParagraph: {
      en: ["select next paragraph", "select the next paragraph"],
      es: [
        "seleccionar siguiente párrafo",
        "seleccionar el siguiente párrafo",
      ],
      he: ["בחר פסקה הבאה", "בחר את הפסקה הבאה", "בחר הפסקה הבאה"],
      ar: ["اختر الفقرة التالية"],
      fr: [
        "sélectionner paragraphe suivant",
        "sélectionnez paragraphe suivant",
      ],
    },
    previousParagraph: {
      en: [
        "select previous paragraph",
        "select the previous paragraph",
      ],
      es: [
        "seleccionar párrafo anterior",
        "seleccionar el párrafo anterior",
      ],
      he: ["בחר פסקה קודמת", "בחר את הפסקה הקודמת", "בחר הפסקה הקודמת"],
      ar: ["اختر الفقرة السابقة"],
      fr: [
        "sélectionner paragraphe précédent",
        "sélectionnez paragraphe précédent",
      ],
    },
    currentField: {
      en: ["select current field"],
      es: ["seleccionar campo actual"],
      he: ["בחר שדה נוכחי"],
      ar: ["اختر الحقل الحالي"],
      fr: ["sélectionner champ actuel", "sélectionnez champ actuel"],
    },
    nextField: {
      en: ["select next field"],
      es: ["seleccionar siguiente campo"],
      he: ["בחר שדה הבא"],
      ar: ["اختر الحقل التالي"],
      fr: ["sélectionner champ suivant", "sélectionnez champ suivant"],
    },
    previousField: {
      en: ["select previous field"],
      es: ["seleccionar campo anterior"],
      he: ["בחר שדה קודם"],
      ar: ["اختر الحقل السابق"],
      fr: ["sélectionner champ précédent", "sélectionnez champ précédent"],
    },
  },
};

// --- Voice macro command: trigger words ---
// English: "Short …" / "Insert …" (DEV: "Insert" replaces "Macro" for clearer STT)
// Hebrew: "קיצור …" / "מאקרו …"
// French: "Raccourci …" / "Macro …"
const MACRO_TRIGGERS_HE = ["קיצור", "מאקרו"];
const MACRO_TRIGGERS_EN = ["short", "insert"];
const MACRO_TRIGGERS_FR = ["raccourci", "macro"];
// Hebrew: only explicit template intent word. Do NOT use "בדיקת" — it occurs
// naturally in medical dictation and caused false template selections.
const TEMPLATE_TRIGGERS_HE = ["תבנית"];
const TEMPLATE_TRIGGER_EN = "template";
const TEMPLATE_TRIGGERS_FR = ["modèle", "template"];

/** Max commands executed from one STT final segment (e.g. "undo bold delete word"). */
const MAX_VOICE_COMMAND_CHAIN = 24;

/**
 * Macro list APIs may return camelCase or PascalCase body HTML.
 * Empty string is treated as missing (cannot insert).
 * @param {object|null|undefined} macro
 * @returns {string}
 */
export const resolveMacroContent = (macro) => {
  if (!macro || typeof macro !== "object") return "";
  const raw = macro.content ?? macro.Content ?? "";
  return raw == null ? "" : String(raw);
};

/**
 * Normalize macro name for matching. Handles:
 * FATTY_LIVER, FATTY LIVER, FATTY-LIVER, Fatty Liver → same key
 * @param {string} name - Macro name (from UI or from speech)
 * @returns {string} - Normalized key: lowercase, underscores/hyphens → space, collapse spaces, trim
 */
export const normalizeMacroName = (name) => {
  if (name == null || typeof name !== "string") return "";
  return name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

/**
 * Strip a leading macro trigger (same boundary rules as template triggers).
 * @returns {{ matched: true, tail: string } | { matched: false, tail: string }}
 */
const consumeLeadingMacroTrigger = (trimmed) => {
  const allTriggers = [
    ...MACRO_TRIGGERS_HE,
    ...MACRO_TRIGGERS_EN,
    ...MACRO_TRIGGERS_FR,
  ];
  const { matched, tail } = consumeLeadingTriggerPhrases(trimmed, allTriggers);
  if (!matched) return { matched: false, tail: trimmed };
  const macroPart = tail.replace(/[.,;:!?]+$/g, "").trim();
  if (!macroPart) return { matched: false, tail: trimmed };
  return { matched: true, tail: macroPart };
};

/**
 * Detect voice macro command after an explicit trigger ("Short …", "Insert …",
 * "קיצור …", "מאקרו …", FR "raccourci"/"macro").
 *
 * Matching uses the same engine as {@link detectVoiceTemplateCommand}:
 * {@link findBestTemplate} with {@link MIN_TEMPLATE_SCORE} and `requireGap: true`,
 * mapping each macro to a pseudo-template (`templateName` = `macroName`). If
 * the engine abstains, falls back to exact normalized name match (incl. FR aliases).
 *
 * @param {string} transcript - Raw transcript (e.g. "Insert fatty liver")
 * @param {Array<{ macroName: string, content: string, ... }>} macros - User's macro list
 * @returns {{ matched: true, macro: Object, macroName: string, residual?: string } | { matched: true, noMacro: true, macroName: string, multiple?: boolean } | null}
 */
export const detectVoiceMacroCommand = (transcript, macros) => {
  if (
    !transcript ||
    typeof transcript !== "string" ||
    !Array.isArray(macros) ||
    macros.length === 0
  ) {
    return null;
  }

  const trimmed = transcript.trim();
  if (!trimmed) return null;

  const { matched: triggerMatched, tail } = consumeLeadingMacroTrigger(
    trimmed,
  );
  if (!triggerMatched) return null;

  const spokenTail = tail.replace(/[.,;:!?]+$/g, "").trim();
  if (!spokenTail) return null;

  const pseudoTemplates = macros
    .filter((m) => {
      const name = m?.macroName ?? m?.MacroName;
      return name != null && String(name).trim();
    })
    .map((m) => {
      const macroName = String(m.macroName ?? m.MacroName).trim();
      return {
        ...m,
        macroName,
        content: resolveMacroContent(m),
        templateName: macroName,
      };
    });

  if (pseudoTemplates.length === 0) {
    return { matched: true, noMacro: true, macroName: spokenTail };
  }

  const result = findBestTemplate(spokenTail, pseudoTemplates, {
    minScore: MIN_TEMPLATE_SCORE,
    requireCoverage: false,
    requireGap: true,
  });

  if (result?.template) {
    return {
      matched: true,
      macro: result.template,
      macroName: spokenTail,
      residual: result.residual ?? "",
    };
  }

  const normalizedSpoken = normalizeMacroName(spokenTail);
  let matches = macros.filter(
    (m) =>
      m &&
      normalizeMacroName(m.macroName ?? m.MacroName) === normalizedSpoken,
  );

  const MACRO_NAME_ALIASES_FR = {
    normale: "normal",
    remarquable: "unremarkable",
  };
  if (matches.length === 0 && MACRO_NAME_ALIASES_FR[normalizedSpoken]) {
    const canonical = MACRO_NAME_ALIASES_FR[normalizedSpoken];
    matches = macros.filter(
      (m) =>
        m &&
        normalizeMacroName(m.macroName ?? m.MacroName) === canonical,
    );
  }

  if (matches.length === 1) {
    const macro = {
      ...matches[0],
      macroName: String(matches[0].macroName ?? matches[0].MacroName).trim(),
      content: resolveMacroContent(matches[0]),
    };
    return { matched: true, macro, macroName: spokenTail };
  }
  if (matches.length > 1) {
    return {
      matched: true,
      noMacro: true,
      macroName: spokenTail,
      multiple: true,
    };
  }
  return { matched: true, noMacro: true, macroName: spokenTail };
};

// ─────────────────────────────────────────────────────────────────────────────
// Template matching: everything below is a thin wrapper around the unified
// engine in `./voiceTemplateEngine.js`. The wrappers handle pre-processing
// (strip trigger / take leading window) and choose thresholds — they do NOT
// do any scoring or residual extraction themselves.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip a leading template trigger word and report whether one was found.
 * @returns {{ matched: boolean, tail: string }}
 */
const consumeLeadingTemplateTrigger = (trimmed) => {
  const allTriggers = [
    ...TEMPLATE_TRIGGERS_HE,
    TEMPLATE_TRIGGER_EN,
    ...TEMPLATE_TRIGGERS_FR,
  ];
  return consumeLeadingTriggerPhrases(trimmed, allTriggers);
};

/**
 * Detect a voice template command introduced by an explicit trigger word
 * ("Template ...", "תבנית ...", "Modèle ...").
 *
 * Delegates the actual template matching to {@link findBestTemplate} — the
 * SAME engine used by {@link detectTriggerlessTemplateCommand}. The only
 * difference is the pre-processing (trigger word stripped here) and the
 * thresholds (relaxed here because the trigger word already signals intent).
 * Sidebar dictation must pass only the session's original first 10 words.
 *
 * @param {string} transcript
 * @param {Array<{ templateName: string, content?: string, templateId?: string }>} templates
 * @returns {null
 *   | { matched: true, template: object, templateName: string, residual: string }
 *   | { matched: true, noTemplate: true, templateName: string }}
 */
export const detectVoiceTemplateCommand = (transcript, templates) => {
  if (
    !transcript ||
    typeof transcript !== "string" ||
    !Array.isArray(templates) ||
    templates.length === 0
  ) {
    return null;
  }

  const trimmed = transcript.trim();
  if (!trimmed) return null;

  const { matched: triggerMatched, tail } = consumeLeadingTemplateTrigger(
    trimmed,
  );
  if (!triggerMatched) return null;

  const spokenTail = tail.replace(/[.,;:!?]+$/g, "").trim();
  if (!spokenTail) return null;

  const result = findBestTemplate(spokenTail, templates, {
    minScore: MIN_TEMPLATE_SCORE,
    requireCoverage: false,
    requireGap: true,
  });

  if (result) {
    return {
      matched: true,
      template: result.template,
      templateName: spokenTail,
      residual: result.residual,
    };
  }
  return { matched: true, noTemplate: true, templateName: spokenTail };
};

/**
 * Template trigger (תבנית / template / …) within the first
 * {@link TRIGGERLESS_WINDOW} words — e.g. "רוצה תבנית CT בטן …".
 */
const findTemplateAfterEmbeddedLeadingTrigger = (transcript, templates) => {
  if (
    !transcript ||
    typeof transcript !== "string" ||
    !Array.isArray(templates) ||
    templates.length === 0
  ) {
    return null;
  }

  const rawWords = transcript.trim().split(/\s+/).filter(Boolean);
  if (rawWords.length === 0) return null;

  const scanLimit = Math.min(TRIGGERLESS_WINDOW, rawWords.length);

  for (let i = 0; i < scanLimit; i += 1) {
    const fromWord = rawWords.slice(i).join(" ");
    const { matched, tail } = consumeLeadingTemplateTrigger(fromWord);
    if (!matched) continue;

    const spokenTail = tail.replace(/[.,;:!?]+$/g, "").trim();
    if (!spokenTail) continue;

    const result = findBestTemplate(spokenTail, templates, {
      minScore: MIN_TEMPLATE_SCORE,
      requireCoverage: false,
      requireGap: true,
      windowSize: TRIGGERLESS_WINDOW,
    });
    if (!result) continue;

    return {
      template: result.template,
      residual: result.residual,
      score: result.score,
    };
  }

  return null;
};

/**
 * Detect a template intent in the first ~8 dictated words WITHOUT requiring
 * a leading trigger at position 0. Supports:
 *   - "{template name} …" (trigger-less token match)
 *   - "… filler … תבנית {template} …" (embedded trigger in the window)
 *   - "רוצה תבנית {template} …" (leading filler skip + window match)
 *
 * Stricter than the explicit-trigger path: score ≥ 0.80 and coverage gate.
 * Sidebar dictation must pass only the session's original first 10 words.
 *
 * @param {string} transcript
 * @param {Array<{ templateName: string, templateId?: string, content?: string }>} templates
 * @returns {null | { template: object, residual: string, score: number }}
 */
export const detectTriggerlessTemplateCommand = (transcript, templates) => {
  if (
    !transcript ||
    typeof transcript !== "string" ||
    !Array.isArray(templates) ||
    templates.length === 0
  ) {
    return null;
  }

  const embedded = findTemplateAfterEmbeddedLeadingTrigger(
    transcript,
    templates,
  );
  if (embedded) {
    return embedded;
  }

  const result = findBestTemplateWithLeadingPrefixSkip(transcript, templates, {
    minScore: MIN_TRIGGERLESS_SCORE,
    requireCoverage: true,
    windowSize: TRIGGERLESS_WINDOW,
    maxPrefixSkip: TRIGGERLESS_MAX_PREFIX_SKIP,
    requireGap: false,
  });
  if (!result) return null;
  return {
    template: result.template,
    residual: result.residual,
    score: result.score,
  };
};

/**
 * Get language code from dictation language format
 * @param {string} dictationLang - Language in dictation format (e.g., "en-US", "es-ES")
 * @returns {string} - Language code (e.g., "en", "es", "he", "ar")
 */
export const getLanguageCode = (dictationLang) => {
  if (!dictationLang) return "en";
  const langCode = dictationLang.split("-")[0].toLowerCase();
  return langCode;
};

/**
 * Whole utterance is exactly "undo" / "redo" (any supported language).
 * Used by AI Edit mode, where only these run locally and all other speech is
 * an edit instruction.
 * @returns {"undo" | "redo" | null}
 */
export const matchExactUndoRedo = (transcript, language) => {
  const langCode = getLanguageCode(language);
  const cleaned = String(transcript || "").replace(/[.,;:!?…"'״׳]+/g, " ");
  if (!normalizeCommandTranscript(cleaned, langCode)) return null;
  // Any supported language: doctors often say "undo" while dictating Hebrew.
  for (const action of ["undo", "redo"]) {
    const byLang = VOICE_COMMANDS[action];
    const langs = [langCode, ...Object.keys(byLang).filter((l) => l !== langCode)];
    for (const l of langs) {
      const said = normalizeCommandTranscript(cleaned, l);
      if ((byLang[l] || []).some((p) => normalizeCommandTranscript(p, l) === said)) return action;
    }
  }
  return null;
};

const pushStaticCommandCandidates = (out, phrases, commandInfo, langCode) => {
  const list = Array.isArray(phrases) ? phrases : [];
  for (const phrase of list) {
    const p = String(phrase || "").trim();
    if (!p) continue;
    out.push({
      phrase: p,
      normalized: normalizeCommandTranscript(p, langCode),
      commandInfo,
      langCode,
    });
  }
};

/** Flat list of static editor commands for exact + fuzzy matching. */
const buildStaticCommandCandidates = (langCode) => {
  const candidates = [];

  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.delete.word[langCode],
    { type: "delete", action: "word" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.delete.line[langCode],
    { type: "delete", action: "line" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.delete.all[langCode],
    { type: "delete", action: "all" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.undo[langCode],
    { type: "edit", action: "undo" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.redo[langCode],
    { type: "edit", action: "redo" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.case.uppercase[langCode],
    { type: "case", action: "uppercase" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.case.lowercase[langCode],
    { type: "case", action: "lowercase" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.formatting.bold[langCode],
    { type: "formatting", action: "bold" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.line.newLine[langCode],
    { type: "line", action: "newLine" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.line.paragraph[langCode],
    { type: "line", action: "paragraph" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.currentSentence[langCode],
    { type: "selection", action: "currentSentence" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.currentParagraph[langCode],
    { type: "selection", action: "currentParagraph" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.nextSentence[langCode],
    { type: "selection", action: "nextSentence" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.previousSentence[langCode],
    { type: "selection", action: "previousSentence" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.nextParagraph[langCode],
    { type: "selection", action: "nextParagraph" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.previousParagraph[langCode],
    { type: "selection", action: "previousParagraph" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.currentField[langCode],
    { type: "selection", action: "currentField" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.nextField[langCode],
    { type: "selection", action: "nextField" },
    langCode,
  );
  pushStaticCommandCandidates(
    candidates,
    VOICE_COMMANDS.selection.previousField[langCode],
    { type: "selection", action: "previousField" },
    langCode,
  );

  return candidates;
};

const getStaticCommandSplitPhrases = (langCode) =>
  buildStaticCommandCandidates(langCode).map((c) => c.phrase);

/**
 * First static editor command at the start of a phrase; may leave remainder for chaining.
 * @returns {{ commandInfo: object, remainder: string } | null}
 */
export const detectVoiceCommandAtStart = (transcript, language = "en") => {
  const trimmed = String(transcript || "").trim();
  if (!trimmed) return null;

  const langCode = getLanguageCode(language);
  const candidates = buildStaticCommandCandidates(langCode);
  const navCommands = VOICE_COMMANDS.navigation["go to"][langCode] || [];
  return findLeadingStaticCommand(
    trimmed,
    candidates,
    navCommands,
    getStaticCommandSplitPhrases(langCode),
    langCode,
  );
};

/**
 * Check if transcript matches a command (case-insensitive; fuzzy best-match for STT typos).
 * @param {string} transcript - Transcript text
 * @param {string} language - Language code (e.g., "en", "es")
 * @returns {Object|null} - Command info or null if no match
 */
export const detectVoiceCommand = (transcript, language = "en") =>
  detectVoiceCommandAtStart(transcript, language)?.commandInfo ?? null;

/**
 * Consume one voice action from the start of `remainder` (macro / template / static command).
 * @returns {{ executed: boolean, remainder: string } | null}
 */
const consumeNextVoiceAction = (
  remainder,
  {
    language,
    handlers,
    insertTextAtCursor,
    macros,
    onVoiceMacroResult,
    templates,
    onVoiceTemplateResult,
    allowTriggerlessTemplate,
    allowTemplateCommands,
    allowMacroCommands,
    allowStaticCommands,
  },
) => {
  const trimmed = String(remainder || "").trim();
  if (!trimmed) return null;

  if (
    allowMacroCommands !== false &&
    Array.isArray(macros) &&
    macros.length > 0 &&
    insertTextAtCursor
  ) {
    const macroResult = detectVoiceMacroCommand(trimmed, macros);
    if (macroResult?.matched) {
      const resolvedContent = resolveMacroContent(macroResult.macro);
      if (macroResult.macro && resolvedContent.trim()) {
        try {
          insertTextAtCursor(resolvedContent);
          onVoiceMacroResult?.({ success: true, macro: macroResult.macro });
        } catch (err) {
          onVoiceMacroResult?.({
            success: false,
            macroName: macroResult.macro.macroName ?? macroResult.macroName,
          });
        }
        return {
          executed: true,
          remainder: String(macroResult.residual ?? "").trim(),
        };
      }
      onVoiceMacroResult?.({
        success: false,
        macroName:
          macroResult.macro?.macroName ?? macroResult.macroName ?? "",
        multiple: macroResult.multiple,
        emptyContent: true,
      });
      return { executed: true, remainder: "" };
    }
  }

  if (
    allowTemplateCommands !== false &&
    Array.isArray(templates) &&
    templates.length > 0 &&
    onVoiceTemplateResult
  ) {
    const templateResult = detectVoiceTemplateCommand(trimmed, templates);
    if (templateResult?.matched) {
      if (templateResult.template) {
        onVoiceTemplateResult?.({
          success: true,
          template: templateResult.template,
          residual: templateResult.residual ?? "",
        });
        return {
          executed: true,
          remainder: String(templateResult.residual ?? "").trim(),
          templateApplied: true,
        };
      }
      onVoiceTemplateResult?.({
        success: false,
        templateName: templateResult.templateName ?? "",
      });
      return { executed: true, remainder: "" };
    }

    if (allowTriggerlessTemplate) {
      const trResult = detectTriggerlessTemplateCommand(trimmed, templates);
      if (trResult) {
        onVoiceTemplateResult?.({
          success: true,
          template: trResult.template,
          residual: trResult.residual,
        });
        return {
          executed: true,
          remainder: String(trResult.residual ?? "").trim(),
          templateApplied: true,
        };
      }
    }
  }

  if (allowStaticCommands !== false) {
    const atStart = detectVoiceCommandAtStart(trimmed, language);
    if (atStart?.commandInfo) {
      executeVoiceCommand(atStart.commandInfo, handlers);
      return {
        executed: true,
        remainder: String(atStart.remainder ?? "").trim(),
      };
    }
  }

  return null;
};

/**
 * Execute voice command based on command info
 * @param {Object} commandInfo - Command information from detectVoiceCommand
 * @param {Object} handlers - Handler functions for each command type
 * @returns {boolean} - True if command was executed, false otherwise
 */
export const executeVoiceCommand = (commandInfo, handlers) => {
  if (!commandInfo || !handlers) return false;

  const { type, action, headerName } = commandInfo;

  try {
    switch (type) {
      case "navigation":
        if (action === "goTo" && handlers.selectContentUnderHeader) {
          return handlers.selectContentUnderHeader(headerName);
        }
        break;

      case "delete":
        if (action === "word" && handlers.deleteWord) {
          return handlers.deleteWord();
        }
        if (action === "line" && handlers.deleteLine) {
          return handlers.deleteLine();
        }
        if (action === "all" && handlers.deleteAll) {
          return handlers.deleteAll();
        }
        break;

      case "edit":
        if (action === "undo" && handlers.undo) {
          return handlers.undo();
        }
        if (action === "redo" && handlers.redo) {
          return handlers.redo();
        }
        break;

      case "case":
        if (action === "uppercase" && handlers.changeTextCase) {
          return handlers.changeTextCase("uppercase");
        }
        if (action === "lowercase" && handlers.changeTextCase) {
          return handlers.changeTextCase("lowercase");
        }
        break;

      case "formatting":
        if (action === "bold" && handlers.toggleBold) {
          return handlers.toggleBold();
        }
        break;

      case "line":
        if (action === "newLine" && handlers.insertNewLine) {
          return handlers.insertNewLine();
        }
        if (action === "paragraph" && handlers.insertNewParagraph) {
          return handlers.insertNewParagraph();
        }
        break;

      case "selection":
        if (action === "currentSentence" && handlers.selectCurrentSentence) {
          return handlers.selectCurrentSentence();
        }
        if (action === "currentParagraph" && handlers.selectCurrentParagraph) {
          return handlers.selectCurrentParagraph();
        }
        if (action === "nextSentence" && handlers.selectNextSentence) {
          return handlers.selectNextSentence();
        }
        if (action === "previousSentence" && handlers.selectPreviousSentence) {
          return handlers.selectPreviousSentence();
        }
        if (
          action === "previousParagraph" &&
          handlers.selectPreviousParagraph
        ) {
          return handlers.selectPreviousParagraph();
        }
        if (action === "nextParagraph" && handlers.selectNextParagraph) {
          return handlers.selectNextParagraph();
        }
        if (action === "currentField" && handlers.selectCurrentField) {
          return handlers.selectCurrentField();
        }
        if (action === "nextField" && handlers.selectNextField) {
          return handlers.selectNextField();
        }
        if (action === "previousField" && handlers.selectPreviousField) {
          return handlers.selectPreviousField();
        }
        break;

      default:
        return false;
    }
  } catch (error) {
    console.error("Error executing voice command:", error);
    return false;
  }

  return false;
};

/**
 * Send language and type preferences to socket
 * @param {Object} socketRef - Socket reference
 * @param {string} language - Language code (e.g., "en-US")
 * @param {string} anathomy - Anatomy type code (e.g., "neuro_brain")
 */
export const sendLanguageAndTypeToSocket = (socketRef, language, anathomy) => {
  if (!socketRef?.current?.connected) {
    console.log("⚠️ Socket not connected, cannot send language/type");
    return;
  }

  try {
    // Send language preference
    socketRef.current.emit("setLanguage", { language: language });

    // Send anathomy type (check for both null/undefined and empty string)
    const hasAnathomy = anathomy && anathomy.trim() !== "";
    if (hasAnathomy) {
      socketRef.current.emit("setType", { type: anathomy.trim() });
    } else {
      socketRef.current.emit("setType", { type: null });
    }
  } catch (error) {
    console.error("Error sending language/type to socket:", error);
  }
};

/**
 * Run macro/template/static voice-command chain (shared by editor and sidebar).
 * Does not insert free dictation — only actions invoked via callbacks / insertTextAtCursor.
 *
 * @param {string} transcript
 * @param {Object} options
 * @param {string} [options.language]
 * @param {Object} [options.handlers] - RTE static command handlers (optional)
 * @param {Function} [options.insertTextAtCursor] - Macro HTML/plain insert
 * @param {Array} [options.macros]
 * @param {Function} [options.onVoiceMacroResult]
 * @param {Array} [options.templates]
 * @param {Function} [options.onVoiceTemplateResult]
 * @param {boolean} [options.allowTriggerlessTemplate=true]
 * @param {boolean} [options.allowTemplateCommands=true]
 * @param {boolean} [options.allowMacroCommands=true] - When false, skip macro/short voice cmds
 * @param {boolean} [options.allowStaticCommands=true]
 * @returns {{ executedAny: boolean, remainder: string }}
 */
export const runVoiceCommandChain = (transcript, options = {}) => {
  const {
    language = "en",
    handlers = {},
    insertTextAtCursor,
    macros,
    onVoiceMacroResult,
    templates,
    onVoiceTemplateResult,
    allowTriggerlessTemplate: initialTriggerless = true,
    allowTemplateCommands = true,
    allowMacroCommands = true,
    allowStaticCommands = true,
  } = options;

  let remainder = String(transcript || "").trim();
  if (!remainder) {
    return { executedAny: false, remainder: "" };
  }

  let executedAny = false;
  let templateApplied = false;
  let allowTriggerlessTemplate = initialTriggerless;

  for (let step = 0; step < MAX_VOICE_COMMAND_CHAIN && remainder; step += 1) {
    const consumed = consumeNextVoiceAction(remainder, {
      language,
      handlers,
      insertTextAtCursor,
      macros,
      onVoiceMacroResult,
      templates,
      onVoiceTemplateResult,
      allowTriggerlessTemplate,
      allowTemplateCommands,
      allowMacroCommands,
      allowStaticCommands,
    });
    allowTriggerlessTemplate = false;

    if (!consumed) break;
    if (consumed.executed) executedAny = true;
    if (consumed.templateApplied) templateApplied = true;
    remainder = String(consumed.remainder ?? "").trim();
  }

  return { executedAny, remainder, templateApplied };
};

/**
 * Process transcript and handle voice commands
 * @param {string} transcript - Transcript text
 * @param {string} language - Language code (e.g., "en-US")
 * @param {string} anathomy - Anatomy type code
 * @param {Object} socketRef - Socket reference
 * @param {Object} handlers - Handler functions for commands
 * @param {Function} insertTextAtCursor - Function to insert text (or HTML for macros)
 * @param {string} currentTranscript - Current editor transcript
 * @param {Object} [voiceMacroOptions] - {
 *   macros: Array<{macroName, content}>,
 *   onVoiceMacroResult: ({ success, macro, macroName }) => void
 * }
 * @param {Object} [options]
 * @param {boolean} [options.allowTextInsertion=true] - When false, unrecognized speech will not be inserted as text.
 * @param {boolean} [options.hasEditorSelection=false] - When true, dictation replaces a selection and must not get a leading space.
 * @param {boolean} [options.needsLeadingDictationSpace=false] - When true, prefix free dictation with a space before the preceding character.
 * @param {boolean} [options.preserveSonioxSpacing=false] - Soniox (he/fr/ar): insert the raw STT delta when no command ran, so leading spaces in token text are kept (matches sidebar).
 * @returns {boolean} - True if command was executed (or macro handled). If text insertion is skipped, returns true.
 */
export const processVoiceTranscript = (
  transcript,
  language,
  anathomy,
  socketRef,
  handlers,
  insertTextAtCursor,
  currentTranscript,
  voiceMacroOptions,
  options,
) => {
  const rawTranscript = String(transcript ?? "");
  if (!rawTranscript || !/\S|\n/.test(rawTranscript)) return false;

  // CRE: the STT server turns spoken "new line" (שורה חדשה) into "\n".
  // Run each line through the normal flow with the editor's new-line action between.
  if (rawTranscript.includes("\n") && typeof handlers?.insertNewLine === "function") {
    const lines = rawTranscript.split(/[ \t]*\n[ \t]*/);
    // insertNewLine applies in a microtask, so later lines are queued after it.
    const runLine = (index) => {
      const line = lines[index];
      if (line && line.trim()) {
        processVoiceTranscript(
          line,
          language,
          anathomy,
          socketRef,
          handlers,
          insertTextAtCursor,
          currentTranscript,
          voiceMacroOptions,
          {
            ...options,
            needsLeadingDictationSpace:
              index === 0 ? options?.needsLeadingDictationSpace : false,
          },
        );
      }
      if (index + 1 < lines.length) {
        handlers.insertNewLine();
        queueMicrotask(() => runLine(index + 1));
      }
    };
    runLine(0);
    return true;
  }

  sendLanguageAndTypeToSocket(socketRef, language, anathomy);

  const macros = voiceMacroOptions?.macros;
  const onVoiceMacroResult = voiceMacroOptions?.onVoiceMacroResult;
  const allowInsert = options?.allowTextInsertion !== false;

  // Report editor (left): macros + static editing commands only.
  // Template selection is intentionally sidebar/transcription-only (right).
  const { executedAny, remainder, templateApplied } = runVoiceCommandChain(
    transcript,
    {
      language,
      handlers,
      insertTextAtCursor,
      macros,
      onVoiceMacroResult,
      allowMacroCommands: true,
      allowTemplateCommands: false,
      allowTriggerlessTemplate: false,
      allowStaticCommands: true,
    },
  );

  // After a template is applied, do not insert trailing dictation into the editor
  // (matches sidebar behaviour: template loads into the report, not the remainder).
  if (
    remainder &&
    allowInsert &&
    insertTextAtCursor &&
    !templateApplied
  ) {
    const hasSelection = options?.hasEditorSelection === true;
    const preserveSonioxSpacing = options?.preserveSonioxSpacing === true;
    const freeDictationText =
      preserveSonioxSpacing && !executedAny ? rawTranscript : remainder;
    const needsSpace =
      !preserveSonioxSpacing &&
      !hasSelection &&
      options?.needsLeadingDictationSpace === true;
    const textToInsert = needsSpace ? ` ${freeDictationText}` : freeDictationText;
    insertTextAtCursor(textToInsert);
    return executedAny;
  }

  if (remainder && !allowInsert) {
    return true;
  }

  return executedAny;
};
